import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export function XeroStatusBadge({ row }) {
  if (row.xero_sync_error) return <Badge variant="destructive" data-testid="xero-status-error" title={row.xero_sync_error}>Error</Badge>;
  if (row.xero_contact_id) return <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid="xero-status-linked">Linked</Badge>;
  return <Badge variant="outline" className="text-amber-700 border-amber-400" data-testid="xero-status-unlinked">Not linked</Badge>;
}

export default function XeroContactsCard({ connected }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/admin/xero/contacts").then((r) => setData(r.data)).catch(() => setData({ contacts: [], summary: { total: 0, linked: 0, errors: 0 } }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const syncAll = async () => {
    setBusy("all");
    try {
      const { data: r } = await api.post("/admin/xero/contacts/sync");
      const msg = `Synced ${r.total} account customer${r.total === 1 ? "" : "s"} — ${r.created} created, ${r.updated} updated, ${r.linked} matched, ${r.unchanged} unchanged${r.pulled.length ? `, ${r.pulled.length} detail${r.pulled.length === 1 ? "" : "s"} pulled from Xero` : ""}`;
      r.errors.length ? toast.warning(`${msg}. ${r.errors.length} error(s) — see the table`) : toast.success(msg);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed"); } finally { setBusy(""); }
  };
  const syncOne = async (row) => {
    setBusy(row.id);
    try {
      const { data: r } = await api.post(`/admin/xero/contacts/${row.id}/sync`);
      toast.success(r.changes?.length ? `Synced — picked up from Xero: ${r.changes.join("; ")}` : `Xero contact ${r.action}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sync failed"); load(); } finally { setBusy(""); }
  };

  const s = data?.summary;
  return (
    <Card data-testid="xero-contacts-card">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-lg">Xero contacts</CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">14-day account, partner and paint shop customers are kept in step with Xero contacts. Nightly at 3am; Xero wins for name & email.</p>
          {s && <p className="mt-1 text-xs text-muted-foreground" data-testid="xero-contacts-summary">{s.linked} of {s.total} linked{s.errors ? ` · ${s.errors} with errors` : ""}{data.last_run ? ` · last run ${fmt(data.last_run.at)} (${data.last_run.trigger})` : ""}</p>}
        </div>
        <Button onClick={syncAll} disabled={!connected || !!busy} data-testid="xero-contacts-sync-all" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
          {busy === "all" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sync all
        </Button>
      </CardHeader>
      <CardContent>
        {!connected && <p className="mb-3 text-sm text-kilted-gold">Connect Xero above to sync contacts.</p>}
        {!data ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" /> : data.contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No account customers yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Type</TableHead><TableHead>Xero</TableHead><TableHead>Last synced</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {data.contacts.map((row) => (
                  <TableRow key={row.id} data-testid={`xero-contact-row-${row.id}`}>
                    <TableCell>
                      <div className="font-medium">{row.business_name || row.name}</div>
                      <div className="text-xs text-muted-foreground">{row.business_name ? `${row.name} · ` : ""}{row.email}</div>
                      {row.xero_sync_error && <div className="mt-0.5 text-xs text-red-700" data-testid={`xero-contact-error-${row.id}`}>{row.xero_sync_error}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{row.type_label}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2"><XeroStatusBadge row={row} />
                        {row.xero_url && <a href={row.xero_url} target="_blank" rel="noreferrer" className="text-kilted-green" title="Open in Xero" data-testid={`xero-contact-open-${row.id}`}><ExternalLink className="h-3.5 w-3.5" /></a>}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmt(row.xero_synced_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => syncOne(row)} disabled={!connected || !!busy} data-testid={`xero-contact-sync-${row.id}`} className="h-8 gap-1">
                        {busy === row.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
